import React from 'react';

function getAndCachePersistentItem(persistentState, key, defaultValue) {
  const item = persistentState.getItem(key);
  if (item !== null) {
    try {
      return JSON.parse(item);
    } catch (e) {
      console.error('Error parsing persisted item', key, item, e);
    }
  }
  return defaultValue;
}

function setPersistentItem(persistentState, key, value) {
  if (value === undefined) {
    persistentState.removeItem(key);
  } else {
    const toStore = JSON.stringify(value);
    persistentState.setItem(key, toStore);
  }
}

/**
 * React hook providing a persistent (per-session) state value and setter
 * (a drop-in replacement for React.useState).
 */
export default function usePersistentState(key, defaultValue) {
  const [value, _setValue] = React.useState(() =>
    // Note: returned value will not update if defaultValue changes
    getAndCachePersistentItem(window.sessionStorage, key, defaultValue),
  );
  const setValue = React.useCallback(
    (newValue) => {
      _setValue(newValue);
      setPersistentItem(window.sessionStorage, key, newValue);
    },
    [key],
  );
  return [value, setValue];
}

/**
 * React hook providing a persistent global state value and setter
 * (a drop-in replacement for React.useState).
 */
export function usePersistentGlobalState(key, defaultValue) {
  const [value, _setValue] = React.useState(() =>
    // Note: returned value will not update if defaultValue changes
    getAndCachePersistentItem(window.localStorage, key, defaultValue),
  );
  const setValue = React.useCallback(
    (newValue) => {
      _setValue(newValue);
      setPersistentItem(window.localStorage, key, newValue);
    },
    [key],
  );
  return [value, setValue];
}
